import { FieldsManager, FormContext, FormInputOptions, OptionsFormField } from "../components.ts";
import { PaymentElement, React, useContext } from "../deps/remix.ts";
import { useEffect, useState } from "react";
import { Label } from "../components.ts";

import { useBindings } from "../hooks/use-bindings.ts";

export const PaymentSelectionOptionsField: React.FC<
  { allowDetatch?: boolean; callback?: (value: string) => void; defaultWasDeclined?: boolean }
> = ({ allowDetatch = false, callback, defaultWasDeclined = false }) => {
  //this lets us know if the client has rendered, only then do we
  //render the payment element. Dont wanna do that on the server.
  const [clientLoaded, setClientLoaded] = useState(false);
  useEffect(() => {
    setClientLoaded(true);
  }, []);

  const manager = useContext(FormContext)!;
  const { currentCustomer } = useBindings();

  const existingPaymentMethods = currentCustomer && currentCustomer.payment_methods
    ? currentCustomer!.payment_methods
    : [];

  const opts = {} as FormInputOptions;
  existingPaymentMethods.forEach((pm) => {
    opts[pm.id] = {
      label: (
        <span className="FormField__PMLabelText">
          {`${pm.brand.toUpperCase()} ending ${pm.last4},`}
          <span>{`exp. ${pm.expiry}`}</span>
        </span>
      ),
      isDefault: pm.isDefault,
    };
  });

  //why is this ever true for a brand new acct?
  const hasPaymentMethods = !!existingPaymentMethods.length;
  const [newPaymentPref, setNewPaymentPref] = useState(
    !existingPaymentMethods.length,
  );

  const needsPaymentPrompt = !!(
    newPaymentPref && !manager.getValue("payment_method")
  ) || !existingPaymentMethods.length;
  return (
    <div>
      <OptionsFormField
        name="payment_method"
        options={opts}
        renderOption={allowDetatch
          ? (opt, field, input) => {
            const isSelected = opt.id === field.value;
            const isDeleting = isSelected && manager.getValue("form_intent") === "delete";
            const optWasDeclined = opt.isDefault && defaultWasDeclined;

            let checkboxClass = "";
            if (isSelected) checkboxClass = "--checked";
            if (isDeleting) checkboxClass = "--checked-deleting";
            return (
              <div className="FormField__PMFieldContainer" key={`payment_method-${opt.id}-label`}>
                {!optWasDeclined && (
                  <Label
                    key={`payment_method-${opt.id}-label`}
                    name="payment_method"
                    htmlFor={`payment_method-${opt.id}`}
                    additionalCSSClasses={`--w-100 --radio ${
                      opt.id === field.value ? "--checked" : ""
                    }`}
                    disableSelect={isDeleting}
                  >
                    <div
                      className={`FormField__FlexContainer --space-between ${
                        isDeleting ? "--danger" : ""
                      }`}
                      onClick={(event) => {
                        manager.setValue("form_intent", "post");
                        callback!("post");
                      }}
                    >
                      <div className="FormField__FlexContainer">
                        <div className="FormField__FakeRadioContainer">
                          {input}
                          <div className={`FormField__FakeRadio ${checkboxClass}`}></div>
                        </div>
                        {opt.label}
                      </div>
                      <Star
                        isDefault={!!opt.isDefault}
                        isSelected={isSelected}
                        manager={manager}
                      />
                    </div>
                  </Label>
                )}
                {optWasDeclined && (
                  <div
                    className={`FormField__Label --radio`}
                  >
                    <div
                      className={`FormField__FlexContainer --space-between --danger`}
                    >
                      <div className="FormField__FlexContainer">
                        <div className="FormField__FakeRadioContainer">
                          <div
                            className={`FormField__FakeRadio`}
                            style={{
                              background: "var(--grey-very-light)",
                              borderColor: "var(--grey-very-light)",
                            }}
                          >
                          </div>
                        </div>
                        WAS DECLINED -
                        {opt.label}
                      </div>
                    </div>
                  </div>
                )}
                {!opt.isDefault && callback &&
                  (
                    <div
                      className={`FormField__PMDeleteContainer ${isSelected ? "--selected" : ""} ${
                        isDeleting ? "--deleting" : ""
                      }`}
                      style={{ flexShrink: 0 }}
                    >
                      <div
                        className={`FormField__PMDelete ${isDeleting ? "--deleting" : ""}`}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (isDeleting) {
                            manager.setValue("form_intent", "post");
                            manager.setValue("payment_method", null as any);
                            callback("post");
                            setNewPaymentPref(false);
                          } else {
                            manager.setValue("form_intent", "delete");
                            manager.setValue("payment_method", opt.id);
                            callback("delete");
                            setNewPaymentPref(false);
                          }
                        }}
                      >
                        <i className="fa-solid fa-circle-minus"></i>
                        <div className="cancel">Cancel</div>
                      </div>
                    </div>
                  )}
              </div>
            );
          }
          : undefined}
      >
        {hasPaymentMethods && (
          <label
            className={`FormField__Label --radio ${needsPaymentPrompt && "--checked"}`}
          >
            <div className="FormField__FlexContainer">
              <div className="FormField__FlexContainer">
                <div className="FormField__FakeRadioContainer">
                  <input
                    type="radio"
                    autoComplete="off"
                    checked={needsPaymentPrompt}
                    onChange={(e) => {
                      if (e.target.checked) {
                        manager.setValue("payment_method", "");
                        setNewPaymentPref(true);
                        if (callback) callback("post");
                      }
                    }}
                  />{" "}
                  <div className={`FormField__FakeRadio ${needsPaymentPrompt && "--checked"}`}>
                  </div>
                </div>
                New Payment Method
              </div>
              <Star
                isDefault={false}
                isSelected={needsPaymentPrompt}
                manager={manager}
              />
            </div>
          </label>
        )}
      </OptionsFormField>
      <div
        style={{
          left: needsPaymentPrompt ? "unset" : "-190000px",
          position: needsPaymentPrompt ? "relative" : "absolute",
          pointerEvents: needsPaymentPrompt ? "all" : "none",
        }}
      >
        {clientLoaded && <PaymentElement />}
      </div>
    </div>
  );
};

function Star(
  { isDefault, isSelected, manager }: {
    isDefault: boolean;
    isSelected: boolean;
    manager: FieldsManager;
  },
) {
  if (!manager.hasParam("form_intent")) return null;

  const isDeleting = manager.getValue("form_intent") === "delete";

  let showFullStar = false;
  if (isDefault && (isSelected || isDeleting)) showFullStar = true;
  if (!isDefault && isSelected && !isDeleting) showFullStar = true;

  let showEmptyStar = false;
  if (isDefault && !isSelected && !isDeleting) showEmptyStar = true;

  return (
    <div>
      <span
        style={showFullStar ? { display: "block" } : { display: "none" }}
      >
        <i className="fa-solid fa-star"></i>
      </span>
      <span
        style={showEmptyStar ? { display: "block" } : { display: "none" }}
      >
        <i className="fa-light fa-star"></i>
      </span>
    </div>
  );
}

export default PaymentSelectionOptionsField;
